import { Component, EventEmitter, Input, Output, inject,
    ChangeDetectionStrategy
} from '@angular/core';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { Utils } from 'src/modules/utils/shared/utils';
import { FrontendFieldAddon } from '../../models/frontend-field-addon.model';
import { FrontendField } from '../../netgen/FrontendField';
import { FrontendFieldListItem } from '../../netgen/FrontendFieldListItem';
import { FrontendFieldType } from '../../netgen/FrontendFieldType';
import { FrontendForm } from '../../netgen/FrontendForm';
import { GenericFieldDefinition } from '../../models/generic-field-definition.model';
import { PlotDefinition } from 'src/modules/enaio/netgen/PlotDefinition';
import { PlotType } from 'src/modules/enaio/netgen/PlotType';
import { PlotReducer } from 'src/modules/enaio/netgen/PlotReducer';
import { Arrays } from 'src/modules/utils/shared/arrays';

@Component({
    selector: 'app-plot-definition',
    templateUrl: './plot-definition.component.html',
    styleUrl: './plot-definition.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PlotDefinitionComponent {

    app = inject(MainAppService);

    @Input()
    templates: PlotDefinition[];
    @Input()
    fields: GenericFieldDefinition[];
    @Input()
    plot: PlotDefinition;

    @Output()
    onStoreTemplate = new EventEmitter<PlotDefinition>();

    form: FrontendForm;

    public generateForm(): void {
        let reducerListItems = [
            new FrontendFieldListItem(PlotReducer.none, "Keiner"),
            new FrontendFieldListItem(PlotReducer.count, "Anzahl")
        ];

        let emptyPlot = new PlotDefinition();
        this.form = new FrontendForm([
            this.templates == null ? null : new FrontendField("template", "Gespeicherte Vorlagen", FrontendFieldType.dropdown, {  noMapping: true, value: emptyPlot,
                onValueChanged: this.selectTemplate.bind(this),
                listItems: [new FrontendFieldListItem(emptyPlot, "--Neu--"), ...this.templates.map(plot => new FrontendFieldListItem(plot, plot.name))],
                addOnButtons: [new FrontendFieldAddon("save", "", "fas fa-save", true, this.storeTemplate.bind(this))] }),
            new FrontendField("xAxisLabel", "X-Achse", FrontendFieldType.string, { hasControl: false, noMapping: true, style: { "font-weight": "bold" } }),
            new FrontendField("xAxis.field", "Feld", FrontendFieldType.dropdown, { 
                mandatory: true, 
                listItems: Arrays.sortBy(this.fields, field => field.name).map(field => new FrontendFieldListItem(field.name, field.name))
            }),
            new FrontendField("xAxis.reducer", "Aggregat", FrontendFieldType.dropdown, { listItems: reducerListItems}),
            new FrontendField("xAxis.field2", "Feld 2 (optional)", FrontendFieldType.dropdown, { 
                listItems: [new FrontendFieldListItem("", "--Ohne--"), ...Arrays.sortBy(this.fields, field => field.name).map(field => new FrontendFieldListItem(field.name, field.name))]
            }),
            new FrontendField("yAxisLabel", "Y-Achse", FrontendFieldType.string, { hasControl: false, noMapping: true, style: { "font-weight": "bold" } }),
            new FrontendField("yAxis.field", "Feld", FrontendFieldType.dropdown, { 
                mandatory: true, 
                listItems: Arrays.sortBy(this.fields, field => field.name).map(field => new FrontendFieldListItem(field.name, field.name))
            }),
            new FrontendField("yAxis.reducer", "Aggregat", FrontendFieldType.dropdown, { listItems: reducerListItems}),
            new FrontendField("yAxis.field2", "Feld 2 (optional)", FrontendFieldType.dropdown, { 
                listItems: [new FrontendFieldListItem("", "--Ohne--"), ...Arrays.sortBy(this.fields, field => field.name).map(field => new FrontendFieldListItem(field.name, field.name))]
            }),
            new FrontendField("colorBy", "Einfärben nach", FrontendFieldType.dropdown, { 
                listItems: [new FrontendFieldListItem("", "--Ohne--"), ...Arrays.sortBy(this.fields, field => field.name).map(field => new FrontendFieldListItem(field.name, field.name))]
            }),
            new FrontendField("type", "Diagrammtyp", FrontendFieldType.dropdown, { 
                listItems: [
                    new FrontendFieldListItem(PlotType.line, "Linie"),
                    new FrontendFieldListItem(PlotType.area, "Fläche"),
                    new FrontendFieldListItem(PlotType.barY, "Balken vertikal"),
                    new FrontendFieldListItem(PlotType.barX, "Balken horizontal"),
                    new FrontendFieldListItem(PlotType.dot, "Punkte"),
                    new FrontendFieldListItem(PlotType.tickY, "Tick vertikal"),
                    new FrontendFieldListItem(PlotType.tickX, "Tick horizontal"),
                    new FrontendFieldListItem(PlotType.pie, "Torte")
                ]
            }),
            new FrontendField("advancedLabel", "Erweitert", FrontendFieldType.string, { hasControl: false, noMapping: true, style: { "font-weight": "bold" } }),
            new FrontendField("opacity", "Deckkraft", FrontendFieldType.string)
        ]);
        if (this.plot.type == null) {
            this.plot.type = PlotType.line;
        }
        this.form.fill(this.plot);
    }

    public getDefinition(): PlotDefinition {
        this.form.get(this.plot);
        this.plot.opacity = Utils.toNumber(this.plot.opacity);
        return this.plot;
    }

    private async storeTemplate(): Promise<void> {
        let name = await this.app.dialog.input<string>(new FrontendField("", "Name", FrontendFieldType.string, { mandatory: true }), "Name der Vorlage");
        if (name != null) {
            let plot = new PlotDefinition();
            this.form.get(plot);
            plot.name = name;
            this.onStoreTemplate.emit(plot);
        }
    }

    private selectTemplate(): void {
        let sel = this.form.getValue("template") as PlotDefinition;
        this.form.fill(sel);
    }
}
